import { Injectable } from '@angular/core';
import { DpFieldTable, DpFormButton, DpFormButtonType, DpFormRow, DpModalForm, DpModalFormService, DpModalFormSize, TableFieldBadge, TableFieldBadges, TableFieldIcon, TableFieldIcons, TableFieldIconsText } from '@docpath/webcomponents';
import { Table, TableField, TableFieldText, TableRow } from '@docpath/webcomponents/table';
import { faComment, faEnvelope } from '@fortawesome/free-regular-svg-icons';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { AlertRule, Via } from '../../configuration/alert-rules/models/alert-rule';
import { MonitoringTableService } from '../components/table/monitoring-table.service';
import { AssignedRule } from '../models/assigned-rule';
import { MonitoringService } from './monitoring.service';
import { MonitoringHttpService } from './monitoring-http.service';

@Injectable({
    providedIn: 'root'
})
export class MonitoringFormService {

    readonly i18nPrefix = 'monitoring.form';
    readonly i18nPrefixTableAlertRule = 'alert-rules-config.table';

    constructor(
        private readonly modalFormService: DpModalFormService,
        private readonly monitoringHttpService: MonitoringHttpService,
        private readonly monitoringService: MonitoringService,
        private readonly monitoringTableService: MonitoringTableService
    ) {
    }

    openAssignAlertRuleForm(dgeId: number, assignedRules: AssignedRule[]): void {
        const form = this.initAssignAlertRuleForm(assignedRules);

        form.submit = (value, activeModal) => {
            this.setAssignedRules(value.rulesAssignment as AlertRule[], assignedRules);
            this.assignAlertRule(dgeId, assignedRules, activeModal);
        };

        const modalReference = this.modalFormService.open(form);

        modalReference.shown.subscribe(() => {
            form.setValue({ rulesAssignment: this.toAssignedRulesFormValue(assignedRules) });
        });
    }

    private setAssignedRules(alertRulesValue: AlertRule[], assignedRulesValue: AssignedRule[]): void {
        assignedRulesValue.forEach(assignedRule => {
            assignedRule.assigned = Boolean(alertRulesValue.find(rule => rule.id === assignedRule.alertRule.id));
        });
    }

    private toAssignedRulesFormValue(assignedRulesValue: AssignedRule[]): unknown {
        const alertRules: AlertRule[] = assignedRulesValue
            .filter(ruleItem => ruleItem.assigned)
            .map(ruleItem => ruleItem.alertRule);
        return { alertRules };
    }

    private assignAlertRule(dgeId: number, assignedRules: AssignedRule[], activeModal: NgbModalRef) {
        this.monitoringHttpService.assignAlertRule(dgeId, assignedRules).subscribe(() => {
            activeModal.close();
            this.monitoringService.notifyAssignedRulesChange();
        });
    }

    private initAssignAlertRuleForm(assignedRules: AssignedRule[]): DpModalForm {
        return new DpModalForm({
            header: `${this.i18nPrefix}.header`,
            rows: this.getAssignAlertRuleFormFields(assignedRules),
            size: DpModalFormSize.Xl,
            i18nPrefix: this.i18nPrefix,
            buttons: this.getRuleAssignemntFormButtons()
        });
    }

    private getAssignAlertRuleFormFields(assignedRules: AssignedRule[]): DpFormRow[] {
        return [
            new DpFormRow([
                new DpFieldTable({
                    name: 'rulesAssignment',
                    table: this.createTable(assignedRules),
                    onSetValue: this.onSetValue,
                    heightScroll: '30vh',
                    column: 12,
                    labelColumn: 12,
                    selectable: true
                })
            ])
        ];
    }

    private getRuleAssignemntFormButtons(): DpFormButton[] {
        return [
            new DpFormButton({ type: DpFormButtonType.Cancel }),
            new DpFormButton({ type: DpFormButtonType.Submit, enabled: true })
        ];
    }

    private createTable(assignedRules: AssignedRule[]): Table {
        const header = this.createHeader();
        const body = this.getTableBody(assignedRules);
        return new Table(header, body);
    }

    private createHeader(): TableRow {
        return new TableRow(
            [
                new TableFieldText(`${this.i18nPrefixTableAlertRule}.rule-name`, undefined, undefined, undefined, undefined, true),
                new TableFieldText(`${this.i18nPrefixTableAlertRule}.rule_description`, undefined, undefined, undefined, undefined, true),
                new TableFieldText(`${this.i18nPrefixTableAlertRule}.alerts`, undefined, undefined, undefined, undefined, true),
                new TableFieldText(`${this.i18nPrefixTableAlertRule}.priority.label`, undefined, undefined, undefined, undefined, true)
            ]
        );
    }

    private getTableBody(assignedRules: AssignedRule[]): TableRow[] {
        return assignedRules.map(assignedRule => {
            const fields: TableField[] = [];
            fields.push(new TableFieldIconsText(
                new TableFieldIcons([new TableFieldIcon(['fas', 'square'], undefined, undefined, undefined, undefined, { style: `color:${assignedRule.alertRule.color} !important` })]),
                new TableFieldText(assignedRule.alertRule.rule_name)));
            fields.push(new TableFieldText(assignedRule.alertRule.rule_description || '', undefined, undefined));
            fields.push(this.getAlertsField(assignedRule.alertRule));
            fields.push(new TableFieldBadges([
                new TableFieldBadge(`${this.i18nPrefixTableAlertRule}.priority.option.${assignedRule.alertRule.priority}`, undefined, { cssClass: this.monitoringTableService.getPriority(assignedRule.alertRule.priority) }, true)
            ]));
            const tableRow = new TableRow(fields);
            tableRow.content = assignedRule.alertRule;
            return tableRow;
        });
    }

    private getAlertsField(alertRule: AlertRule): TableFieldIcons {
        const alertsFieldData: Array<TableFieldIcon> = [];

        alertRule.rule_alias.forEach(alias => {
            if (alias.via === Via.Email) {
                const emailIcon = new TableFieldIcon(faEnvelope, `${alias.alias.alert_alias} - ${alias.alias.email}`, undefined, undefined, undefined, { cssClass: 'ms-2 text-dpmedium' });
                alertsFieldData.push(emailIcon);
            } else if (alias.via === Via.SMS) {
                const smsIcon = new TableFieldIcon(faComment, `${alias.alias.alert_alias} - ${alias.alias.phone_number}`, undefined, undefined, undefined, { cssClass: 'ms-2 text-dpmedium' });
                alertsFieldData.push(smsIcon);
            }
        });

        return new TableFieldIcons(alertsFieldData);
    }

    private onSetValue(data: { alertRules?: AlertRule[]; }, table: Table): void {
        const alertRules: AlertRule[] = data.alertRules || [];
        alertRules.forEach(alertRule => {
            const row = table.body.find(row => row.content.id === alertRule.id);

            if (row) {
                row.selected = true;
            }
        });
        table.header.selected = table.body.every(row => row.selected);
    }

}
